import { Badge } from '@mantine/core';
import type { BadgeProps } from '@mantine/core';

type CuisineBadgeProps = {
  cuisine: string;
  /**
   * Where the badge sits. 'body' is the default soft orange chip used in a
   * card's text; 'overlay' is the solid version for sitting on a photo.
   */
  placement?: 'body' | 'overlay';
  size?: BadgeProps['size'];
  style?: React.CSSProperties;
};

/**
 * The cuisine chip on a recipe.
 *
 * One component because the surfaces showing it had drifted into three
 * different looks — grey outline in the cookbook, archive, quick bites and the
 * plan cards; an uncoloured filled badge over photos in the catalog browser,
 * seed queue and public profiles; soft orange in the Taste Tuner and onboarding
 * preview. Same information, three treatments, depending on where you happened
 * to be standing.
 *
 * Soft orange won, so that's the default. It's the treatment the swiping
 * surfaces already used, and orange is the app's own accent rather than the
 * grey an outline badge falls back to.
 *
 * Overlay is a separate placement rather than an oversight. Mantine's `light`
 * variant is a 10%-alpha background, which reads fine on white and disappears
 * on a photograph — the picture shows straight through and the orange text
 * lands on whatever is underneath it. Badges over an image stay `filled`, and
 * share the colour rather than the whole treatment.
 *
 * No border, which is also deliberate. The dietary and tag badges beside this
 * one are `outline` and do have one, so this is the only unbordered pill in the
 * row — but adding a matching border was tried and reverted. It made the badge
 * read as *nearly* a dietary badge instead of clearly the same or clearly
 * different, which is worse. The soft fill is what sets it apart, so a border
 * can't fix that; only dropping the fill (plain `outline`, no distinction at
 * all) or going solid (`filled`, unmistakably a different class) would, and
 * neither was judged worth the change.
 */
export const CuisineBadge = ({
  cuisine,
  placement = 'body',
  size = 'sm',
  style,
}: CuisineBadgeProps) => (
  <Badge
    size={size}
    variant={placement === 'overlay' ? 'filled' : 'light'}
    color="orange"
    style={style}
  >
    {cuisine}
  </Badge>
);
